import React, { useEffect, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { FaSearch } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { View } from "lucide-react";
import RemarksModal from "../Form/FormFields/RemarksModal";
interface TableProps {
  columns: any[];
  title: string;
  fetchData: any[];
  id: string;
  rows: [];
}
const formatDateTime = (date: string) =>
  format(new Date(date), "dd/MM/yyyy HH:mm");
const getStatusColor = (status: string) => {
  const colors: any = {
    Approved: "bg-green-100 text-green-700 border border-green-500",
    Pending: "bg-yellow-100 text-yellow-700 border border-yellow-500",
    Rejected: "bg-red-100 text-red-700 border border-red-500",
  };
  return colors[status] || "bg-gray-100 text-gray-700 border border-gray-500";
};
const formatNumberWithCommas = (value: any) => {
  const locale = "en-IN";
  if (value == null) return 0;
  if (typeof value === "number") return value.toLocaleString(locale);
  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "");
    if (/^-?\d+(\.\d+)?$/.test(cleaned))
      return Number(cleaned).toLocaleString(locale);
  }
  return value;
};
const DynamicTable: React.FC<TableProps> = ({
  columns,
  fetchData,
  id,
  title,
  rows,
}) => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const [chartTypeId, setChartTypeId] = useState(false);
  const [modalData, setModalData] = useState("");
  const [data, setData] = useState<any[]>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const selectorData: any = useSelector((state: any) => state?.dynamicData);

  const closeModal = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    setData(rows || []);
    if (fetchData?.data?.length > 0) {
      setData(selectorData?.[id]?.data || []);
    }
  }, [fetchData, selectorData?.[id]]);
  // Remove zero columns
  const removeZeroColumns = (data: any[], columns: any[]) => {
    if (!data || data.length === 0) return columns;
    const isZero = (v: any) =>
      v === 0 || v === "0" || v == null || v === "" || v === "null";
    const process = (cols: any[]) =>
      cols
        .map((col) => {
          if (col.columns) {
            const children = process(col.columns);
            return children.length ? { ...col, columns: children } : null;
          }
          const allZero = data.every((row) => isZero(row[col.field]));
          return allZero ? null : col;
        })
        .filter(Boolean);
    return process(columns);
  };
  const formattedColumns = React.useMemo(() => {
    const filtered = removeZeroColumns(data, columns);
    const convert = (cols: any[]) =>
      cols.map((col) =>
        col.columns
          ? { header: col.header, columns: convert(col.columns) }
          : {
              accessorKey: col.field,
              header: col.header,
            }
      );
    return convert(filtered);
  }, [columns, data]);
  const table = useReactTable({
    data,
    columns: formattedColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
  });
  return (
    <>
      <div className="overflow-x-auto">
        {/* Search + Title */}
        <div className="p-4 rounded-xl shadow-md bg-white flex justify-between items-center">
          <label className="font-roboto text-[16px] text-[#031B4E] font-semibold">
            {title}
          </label>
          <div className="flex items-center gap-2 border border-[#EBECEF] rounded-lg px-4 py-2 shadow-sm">
            <FaSearch className="text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="outline-none bg-transparent"
            />
          </div>
        </div>
        {/* Main Table */}
        <div className="overflow-y-auto mt-3">
          <table className="w-full text-left">
            <thead className="bg-gray-800 text-white text-center sticky top-0">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className="px-4 py-2 border border-gray-400"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-gray-50">
                    {row.getVisibleCells().map((cell, i) => {
                      const rawValue = cell.getValue();
                      let display = rawValue;
                      if (cell.column.id === "actions") {
                        display = (
                          <div className="flex items-center justify-center gap-3">
                            {/* View Button */}
                            <button
                              onClick={() => {
                                setModalData(row.original);
                                setChartTypeId(row.original.chart_type_id);
                                setIsOpen(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 hover:scale-110 transition"
                              title="View"
                            >
                              <View size={18} />
                            </button>
                          </div>
                        );
                      } else if (cell.column.id === "created_at") {
                        display = formatDateTime(rawValue);
                      } else if (cell.column.id === "status") {
                        display = (
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(
                              rawValue
                            )}`}
                          >
                            {rawValue}
                          </span>
                        );
                      } else if (
                        typeof rawValue === "number" ||
                        rawValue == null
                      ) {
                        display = formatNumberWithCommas(rawValue);
                      } else if (typeof rawValue === "string") {
                        const cleaned = rawValue.replace(/,/g, "");
                        if (/^-?\d+(\.\d+)?$/.test(cleaned))
                          display = formatNumberWithCommas(rawValue);
                      }
                      return (
                        <td
                          key={i}
                          className="p-1 border-b border-[#EBECEF] text-center"
                        >
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length + 2}
                    className="p-3 text-center text-gray-500"
                  >
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <RemarksModal
        modalIsOpen={modalIsOpen}
        closeModal={closeModal}
        modalData={modalData}
        chartTypeId={chartTypeId}
      />
    </>
  );
};

export default DynamicTable;
